<template>
  <div>
    <v-app-bar color="primary" flat app dark>
      <v-app-bar-nav-icon style="position: absolute" @click="goBack">
        <v-icon size="large" color="white">mdi-chevron-left</v-icon>
      </v-app-bar-nav-icon>
      <v-spacer />
      <v-app-bar-title style="min-width: fit-content; font-size: 14px;">
        VEVOレポート - {{ monthsLabel || `第${quarter}Q` }}
      </v-app-bar-title>
      <v-spacer />
    </v-app-bar>

    <v-main>
      <div class="ma-3 text-body-2 text-grey">
        {{ year }}年度・第{{ quarter }}クォーター ({{ monthsLabel }})
      </div>

      <v-progress-linear v-if="isFetching" indeterminate color="primary" />

      <div v-if="quarterDetail" class="ma-3 d-flex flex-column" style="gap: 0.75rem;">
        <template v-for="cls in quarterDetail.classes" :key="cls.classId">
          <div class="text-subtitle-2 font-weight-bold mt-2 mb-1 text-grey-darken-2">
            {{ cls.className }}
            <span class="text-caption text-grey">({{ cls.ageLabel }} / {{ cls.children.length }}名)</span>
          </div>
          <v-card
            v-for="c in cls.children"
            :key="c.childId"
            variant="outlined"
            class="pa-3"
            :class="{ 'ai-report-card--clickable': c.status !== 'notGenerated' }"
            @click="toDetail(c.childId)"
          >
            <div class="d-flex align-center" style="gap: 0.5rem;">
              <ai-report-status-chip :status="c.status" />
              <span class="text-body-1 font-weight-bold">
                {{ c.lastName }} {{ c.firstName }}
              </span>
              <v-spacer />
              <v-icon color="primary">mdi-chevron-right</v-icon>
            </div>

            <div class="mt-1 d-flex align-center flex-wrap text-caption text-grey" style="gap: 0.5rem 1rem;">
              <span>達成項目数: {{ c.achievedItemCount }}個</span>
              <span>経過記録数: {{ c.progressRecordCount }}件</span>
              <span v-if="c.status !== 'notGenerated'">
                生成回数: {{ c.generationCount }}回
              </span>
            </div>

            <div v-if="c.lastPublishedAt" class="text-caption text-grey">
              公開日: {{ formatDate(c.lastPublishedAt) }}
            </div>
          </v-card>
        </template>

        <v-alert
          v-if="quarterDetail.classes.length === 0"
          type="info"
          variant="outlined"
        >
          対象クォーターのクラスがありません。
        </v-alert>
      </div>
    </v-main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import AiReportStatusChip from "@/components/molecules/AiReportStatusChip.vue";
import { aiReportModule } from "@/store/dataModules/aiReportModule";
import type { AiReportQuarter } from "@/api/ApiAiReport";

interface DataType {
  year: number;
  quarter: AiReportQuarter;
  isFetching: boolean;
}

export default defineComponent({
  components: { AiReportStatusChip },
  data: (): DataType => ({
    year: 0,
    quarter: 1 as AiReportQuarter,
    isFetching: false,
  }),
  computed: {
    quarterDetail() {
      return aiReportModule.quarterDetail;
    },
    monthsLabel(): string {
      return this.quarterDetail ? this.quarterDetail.monthsLabel : "";
    },
  },
  watch: {
    "$route.params": {
      handler() {
        this.readRoute();
        this.load();
      },
    },
  },
  mounted() {
    this.readRoute();
    this.load();
  },
  methods: {
    readRoute() {
      const y = parseInt(this.$route.params.businessYear as string, 10);
      const q = parseInt(this.$route.params.quarter as string, 10) as AiReportQuarter;
      if (!Number.isNaN(y)) this.year = y;
      if ([1, 2, 3, 4].includes(q)) this.quarter = q;
    },
    async load() {
      if (!this.year || !this.quarter) return;
      this.isFetching = true;
      try {
        await aiReportModule.fetchQuarter({
          businessYear: this.year,
          quarter: this.quarter,
        });
      } finally {
        this.isFetching = false;
      }
    },
    toDetail(childId: number) {
      this.$router.push(`/ai-report/detail/${this.year}/${this.quarter}/${childId}`);
    },
    goBack() {
      this.$router.push("/ai-report");
    },
    formatDate(value: string | null): string {
      if (!value) return "—";
      return dayjs(value).format("YYYY/MM/DD HH:mm");
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-report-card--clickable {
  cursor: pointer;
}
</style>
